import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import * as _ from 'lodash';

import { LanguageService, OrganizationType, ScrollService, SegmentService } from 'services';
import { BranchToCreate } from 'models/organization.model';
import { AutoUnsubscribe } from 'helpers';

import { AuthenticationModel, SignupRequest } from '../../../../authentication/authentication.model';
import { AddressModel } from '../../../../order-management/retailer-platform/manage-stores/manage-stores.types';
import { ReferralType, SelectedOrganization, SignupErrorType } from '../../../signup/types';

@Component({
    selector: 'fc-signup-form',
    templateUrl: './signup-form.component.html',
    styleUrls: ['./signup-form.component.less'],
    standalone: false
})
@AutoUnsubscribe
export class SignupFormComponent implements OnInit {
    organizationTypes = OrganizationType;
    organizationTypeEditable = true;
    step = null;
    initStep = 0;
    referrer: string;
    referral: string;
    referralType: ReferralType;
    totalSteps: number;
    signupForm = this.formBuilder.group({
        organizationType: new FormControl(
            null,
            Validators.pattern(`${OrganizationType.Retailer}|${OrganizationType.Manufacturer}`)
        ),
        organization: []
    });

    selectedOrganization: SelectedOrganization;
    error$ = new BehaviorSubject(null);
    userIsJoiningExistingOrganization = false;
    isInOrderSignupFlow = false;

    private _subscription = new Subscription();
    private _signedUpUser: {
        firstName: string;
        surname: string;
        email: string;
        termsConditionsConsent: boolean;
        marketingEmailConsent: boolean;
    } = null;

    constructor(
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private segmentService: SegmentService,
        private languageService: LanguageService,
        private scrollService: ScrollService,
        private authenticationModel: AuthenticationModel,
        private authService: AuthService
    ) {}

    ngOnInit(): void {
        this._subscription.add(
            this.route.queryParamMap.subscribe((params) => {
                this.referralType = params.get('referralType') as ReferralType;
                this.isInOrderSignupFlow = this.referralType === ReferralType.Order;

                this.referral = params.get('referralId');
                this.referrer = params.get('referrer');

                const organizationType = params.get('organizationType');
                if (
                    !_.isEmpty(organizationType) &&
                    Object.values(OrganizationType)
                        .map((type) => type.toString())
                        .includes(organizationType)
                ) {
                    this.signupForm.controls.organizationType.setValue(organizationType);
                    this.organizationTypeEditable = false;
                }

                this.totalSteps = 2;
                this.initStep = Number(params.get('step')) || this.initStep;
                this.gotoStep(this.initStep);
            })
        );
        this._subscription.add(
            this.authService.user$
                .pipe(
                    filter((user) => !!user),
                    // Includes AppUser app-metadata
                    switchMap((user) => this.authenticationModel.getAppUser(user.sub))
                )
                .subscribe((user) => {
                    this._signedUpUser = {
                        firstName: user?.givenName,
                        surname: user?.familyName,
                        email: user?.email,
                        termsConditionsConsent: _.get(user, 'appMetadata.termsConditionsConsent'),
                        marketingEmailConsent: _.get(user, 'appMetadata.marketingEmailConsent', false)
                    };
                })
        );
    }

    onOrganizationLookupClicked({ step, organization }) {
        if (organization) {
            this.selectedOrganization = organization;
            this.selectedOrganization.type = this.signupForm.value.organizationType;
            this.trackSignupOrganization(organization);
        }
        this.gotoStep(step);
    }

    onExistingOrgFinishRequestClicked(error?) {
        if (error) {
            const errorType: string = _.get(error, 'data.type', SignupErrorType.UnknownError);
            this.error$.next(errorType);
            return;
        }
        this.userIsJoiningExistingOrganization = true;
        this.gotoStep(this.totalSteps + 1);
    }

    onOrganizationFormSubmitted(form: FormGroup) {
        if (form) this.trackSignupNewOrganization(form.value);

        this.signupForm.value.organization = form;

        if (!this.isInOrderSignupFlow) {
            return this.onLastFormSubmitted(form);
        }

        this.gotoStep(2);
    }

    gotoStep(step) {
        this.step = step;
        this.error$.next(null);
        this.scrollService.scrollElementTop('fc-signup-form-container');
    }

    retry() {
        this.gotoStep(this.initStep);
    }

    async onLastFormSubmitted(form: FormGroup) {
        const organizationFormValues = this.signupForm.value.organization.value;

        const branch = this.isInOrderSignupFlow ? this.buildBranch(form) : undefined;

        const signupRequest: SignupRequest = {
            organization: {
                name: organizationFormValues.organizationName.value,
                type: this.signupForm.value.organizationType,
                phone: organizationFormValues.organizationPhone,
                website: organizationFormValues.organizationWebsite,
                ...(organizationFormValues.organizationGln ? { gln: organizationFormValues.organizationGln } : {}),
                address: {
                    line1: organizationFormValues.organizationLine1,
                    line2: organizationFormValues.organizationLine2,
                    zip: organizationFormValues.organizationZip,
                    city: organizationFormValues.organizationCity,
                    country: organizationFormValues.organizationCountry.value
                },
                branch
            },
            user: {
                firstname: this._signedUpUser.firstName,
                surname: this._signedUpUser.surname,
                email: this._signedUpUser.email,
                preferredLanguage: this.languageService.getCurrentLanguage()
            },
            termsAccepted: this._signedUpUser.termsConditionsConsent,
            marketingEmailConsentAccepted: this._signedUpUser.marketingEmailConsent,
            signupReason: organizationFormValues.signupReason,
            ...(this.referrer ? { referrer: this.referrer } : {}),
            ...(this.referral ? { referral: this.referral } : {})
        };

        try {
            await this.authenticationModel.submitSignupRequest(signupRequest);
            this.gotoStep(this.totalSteps + 1);
            this.signupForm.reset();
            this.trackSignupRequest(signupRequest);
        } catch (error) {
            const errorType: string = _.get(error, 'data.type', SignupErrorType.UnknownError);
            if (
                errorType === SignupErrorType.SignupRequestAlreadyExistsError &&
                this.step === 2 &&
                this.isInOrderSignupFlow
            ) {
                return this.gotoStep(1);
            }
            this.error$.next(errorType);
        }
    }

    async onStoreFormSubmitted(form: FormGroup) {
        return this.onLastFormSubmitted(form);
    }

    private buildBranch(form: FormGroup): BranchToCreate {
        const name = form.value.name;
        const phone = form.value.phone;
        const glnForm = form.get('glnForm');
        const gln = glnForm.value.gln || '';
        const useFcGln = glnForm.get('useFcGln').value;
        form.value.country = form.value.country.value;
        const address = _.omit(_.assign(form.value as AddressModel, { gln, phone }), ['name']);

        const buyerGln = glnForm.get('buyerGln').value;
        const billingGln = glnForm.get('billingGln').value;

        const branch = {
            address,
            name,
            phone,
            useFcGln,
            buyerGln: !useFcGln && !_.isEmpty(buyerGln) ? buyerGln : null,
            billingGln: !useFcGln && !_.isEmpty(billingGln) ? billingGln : null
        };

        return branch;
    }

    private trackSignupRequest(signupRequest: SignupRequest) {
        const eventName = '[Signup] Signup request created';

        const eventPayload = {
            orgType: signupRequest.organization.type === 'manufacturer' ? 'brand' : signupRequest.organization.type,
            signupType: this.referral ? 'referral Signup' : 'normal signup'
        };
        this.segmentService.track(eventName, eventPayload);
    }

    private trackSignupOrganization(organization) {
        const eventName = organization
            ? '[Signup] existing organisation selected'
            : '[Signup] new org creation started';

        const { value } = this.signupForm;

        const eventPayload = {
            orgType: value.organizationType === 'manufacturer' ? 'brand' : value.organizationType,
            signupType: this.referral ? 'referral Signup' : 'normal signup'
        };
        this.segmentService.track(eventName, eventPayload);
    }

    private trackSignupNewOrganization(organization) {
        const eventName = '[Signup] new organization filled';

        const { value } = this.signupForm;
        const { organizationName, organizationCountry } = organization;

        const eventPayload = {
            orgType: value.organizationType === 'manufacturer' ? 'brand' : value.organizationType,
            signupType: this.referral ? 'referral Signup' : 'normal Signup',
            orgName: organizationName.value,
            country: organizationCountry.value
        };
        this.segmentService.track(eventName, eventPayload);
    }
}
